import { Link, Outlet, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, ArrowRight, GitCompare, X, ChevronDown, Menu } from 'lucide-react';
import { useCompare } from '../../hooks/useCompare';
import { AudiencePrompt } from '../../components/AudiencePrompt';
import RouteSeo from '../../lib/seo';

const CompareTray = () => {
  const { slugs, clear } = useCompare();
  if (slugs.length === 0) return null;
  return (
    <div className="hidden md:flex fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-[#00A79D] text-white rounded-full shadow-lg items-center gap-3 pl-5 pr-2 py-2 max-w-[calc(100vw-1.5rem)]" data-testid="compare-tray">
      <span className="text-xs sm:text-sm font-semibold inline-flex items-center gap-2 whitespace-nowrap"><GitCompare className="w-4 h-4 shrink-0" /> {slugs.length} selected to compare</span>
      <Link to={`/compare?slugs=${slugs.join(',')}`} className="bg-white text-[#00A79D] text-sm font-semibold px-4 py-1.5 rounded-full hover:bg-gray-100 transition-colors" data-testid="compare-tray-view">Compare</Link>
      <button onClick={clear} className="p-1.5 hover:bg-white/15 rounded-full" data-testid="compare-tray-clear" aria-label="Clear compare"><X className="w-4 h-4" /></button>
    </div>
  );
};

const MobileCtaBar = () => {
  const { pathname } = useLocation();
  const { slugs, clear } = useCompare();
  if (pathname === '/get-started') return null;      // redundant on the CTA page itself
  const comparing = slugs.length > 0;
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[#E5E7EB] px-4 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))]" data-testid="mobile-cta-bar">
      {comparing ? (
        <div className="flex items-center gap-2">
          <Link to={`/compare?slugs=${slugs.join(',')}`}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white text-sm font-semibold py-3 rounded-lg shadow-sm"
            data-testid="mobile-cta-compare">
            <GitCompare className="w-4 h-4" /> Compare {slugs.length} {slugs.length === 1 ? 'provider' : 'providers'}
          </Link>
          <button onClick={clear} aria-label="Clear compare"
            className="p-3 border border-[#E5E7EB] rounded-lg text-gray-500 hover:text-red-500 hover:border-red-200 shrink-0"
            data-testid="mobile-cta-compare-clear"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <Link to="/get-started"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white text-sm font-semibold py-3 rounded-lg shadow-sm"
          data-testid="mobile-cta-shortlist">
          Get my shortlist <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
};

const CtcMark = ({ className }) => (
  <svg viewBox="0 0 64 64" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <clipPath id="ctc-mark-clip"><rect width="64" height="64" rx="15" /></clipPath>
    <g clipPath="url(#ctc-mark-clip)">
      <rect width="32" height="64" fill="#1A3A5F" />
      <rect x="32" width="32" height="64" fill="#00A79D" />
    </g>
    <path d="M17 33.5 L27 43.5 L47 21" stroke="#FFFFFF" strokeWidth="6.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Wordmark = ({ dark = false }) => (
  <span className="flex flex-col leading-none">
    <span className="ctc-wordmark font-extrabold text-lg sm:text-xl tracking-tight leading-tight">
      <span className={dark ? 'text-white' : 'text-[#1A3A5F]'}>care</span>
      <span className="text-[#00A79D]">tech</span>
      <span className={dark ? 'text-white' : 'text-[#1A3A5F]'}>compare</span>
    </span>
    <span className={`text-[10px] font-semibold mt-1 inline-flex items-center gap-1 whitespace-nowrap ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
      From the team behind
      <img src="/brand/ctg-c-mark-transparent.png" alt="" className="h-3.5 w-auto inline-block" />
      CareTechGuide
    </span>
  </span>
);

const MENUS = [
  { label: 'Providers', testid: 'nav-care-providers', href: '/browse', items: [
    { to: '/browse', label: 'Find care tech', desc: 'Browse the full supplier directory' },
    { to: '/how-to-choose', label: 'How to choose', desc: 'Guidance for buyers' },
    { to: '/compare', label: 'Compare platforms', desc: 'Side-by-side comparison' },
    { to: '/procurement', label: 'Procurement services', desc: 'Energy, staffing, PPE and more' },
    { to: '/advisor', label: 'CareTechAdvisor', desc: 'Your care tech companion' },
  ] },
  { label: 'Suppliers', testid: 'nav-tech-suppliers', items: [
    { to: '/browse?claim=1', label: 'Find & claim your listing', desc: 'Search the directory and claim your company' },
    { to: '/for-suppliers', label: 'List your product', desc: 'Get in front of care buyers' },
    { to: '/pricing', label: 'Pricing', desc: 'Verified plans by team size' },
    { to: '/assurance', label: 'Supplier Assurance', desc: 'Build trust with buyers' },
  ] },
  { label: 'Resources', testid: 'nav-resources', items: [
    { to: '/knowledge-hub', label: 'Knowledge Hub', desc: 'Guides, comparisons & tools' },
    { to: '/knowledge-hub?guides=1', label: 'Care Tech Guides', desc: 'CareTechGuide.co.uk buyer guides' },
    { to: '/podcast', label: 'Podcast', desc: 'Conversations with care tech suppliers' },
    { to: '/webinars', label: 'Webinars', desc: 'Free live platform walkthroughs' },
  ] },
];

const NavDropdown = ({ label, items, testid, href }) => {
  const [open, setOpen] = useState(false);
  const labelClass = "flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-[#00A79D] transition-colors whitespace-nowrap";
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      {href ? (
        <Link to={href} className={labelClass} data-testid={testid}>
          {label} <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </Link>
      ) : (
        <button className={labelClass} data-testid={testid}>
          {label} <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      )}
      {open && (
        <div className="absolute left-0 top-full pt-3 w-64 z-50" data-testid={`${testid}-menu`}>
          <div className="bg-white border border-[#E5E7EB] rounded-lg shadow-lg py-2">
            {items.map((it) => (
              <Link key={it.to} to={it.to} className="flex flex-col px-4 py-2.5 hover:bg-[#00A79D]/5 transition-colors"
                data-testid={`nav-item-${it.to.slice(1).replace(/\//g, '-')}`}>
                <span className="text-sm font-semibold text-[#1A3A5F]">{it.label}</span>
                <span className="text-xs text-gray-500">{it.desc}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const PublicLayout = () => {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [regLive, setRegLive] = useState(false);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/review-register/status`).then((r) => setRegLive(!!r.data.live)).catch(() => {});
  }, []);
  const menus = regLive ? MENUS.map((m) => (m.label === 'Providers'
    ? { ...m, items: [...m.items, { to: '/independently-reviewed', label: 'CTC independently reviewed', desc: 'Register of products we have reviewed' }] } : m)) : MENUS;

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  // Website analytics: record a public-site page view on every route change.
  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    let sid = localStorage.getItem('ctc_sid');
    if (!sid) {
      sid = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('ctc_sid', sid);
    }
    const q = new URLSearchParams(window.location.search);
    const rc = q.get('ctc_r');
    if (rc) {
      localStorage.setItem('ctc_r', rc);
      q.delete('ctc_r');
      const qs = q.toString();
      window.history.replaceState(window.history.state, '', `${pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
    }
    axios.post(`${API}/public/track/pv`, {
      path: pathname, ref: document.referrer || '', sid, title: document.title || '',
      utm: q.get('utm_source') || '', campaign: q.get('utm_campaign') || '',
      r: localStorage.getItem('ctc_r') || '',
    }).catch(() => {});
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#F9FAFB] ctg-public" data-testid="public-site">
      <RouteSeo />
      <AudiencePrompt pathname={pathname} />
      <header className="sticky top-0 z-40 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="public-logo">
            <CtcMark className="h-9 sm:h-11 w-auto shrink-0" />
            <Wordmark />
          </Link>
          <nav className="hidden md:flex items-center gap-5 lg:gap-6">
            {menus.map((m) => <NavDropdown key={m.label} label={m.label} items={m.items} testid={m.testid} href={m.href} />)}
            <Link to="/knowledge-hub?guides=1" className="text-sm font-semibold text-[#00857D] hover:text-[#00A79D] transition-colors whitespace-nowrap" data-testid="nav-buyers-guides">Buyer's Guides</Link>
            <Link to="/about" className="text-sm font-medium text-gray-600 hover:text-[#00A79D] transition-colors whitespace-nowrap" data-testid="nav-about">About</Link>
            <Link to="/contact" className="text-sm font-medium text-gray-600 hover:text-[#00A79D] transition-colors whitespace-nowrap" data-testid="nav-contact">Contact</Link>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/browse" className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-[#00A79D]" aria-label="Search directory">
              <Search className="w-4 h-4" />
            </Link>
            <Link to="/portal" className="text-sm font-medium text-gray-600 hover:text-[#00A79D] whitespace-nowrap" data-testid="public-member-login">Login</Link>
            <Link to="/get-started"
              className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors whitespace-nowrap"
              data-testid="public-cta-header">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <button
            className="md:hidden p-2 -mr-2 text-[#1A3A5F] shrink-0"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-[#E5E7EB] bg-white shadow-lg" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-5 max-h-[calc(100vh-5rem)] overflow-y-auto">
              {menus.map((m) => (
                <div key={m.label}>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">{m.label}</p>
                  <div className="flex flex-col">
                    {m.items.map((it) => (
                      <Link key={it.to} to={it.to} onClick={() => setMobileOpen(false)}
                        className="py-2 text-[15px] font-medium text-[#1A3A5F] hover:text-[#00A79D]"
                        data-testid={`m-nav-item-${it.to.slice(1).replace(/[/?=]/g, '-')}`}>{it.label}</Link>
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex flex-col border-t border-[#E5E7EB] pt-3">
                <Link to="/knowledge-hub?guides=1" onClick={() => setMobileOpen(false)} className="py-2 text-[15px] font-semibold text-[#00857D] hover:text-[#00A79D]" data-testid="m-nav-buyers-guides">Buyer's Guides</Link>
                <Link to="/about" onClick={() => setMobileOpen(false)} className="py-2 text-[15px] font-medium text-[#1A3A5F] hover:text-[#00A79D]" data-testid="m-nav-about">About</Link>
                <Link to="/contact" onClick={() => setMobileOpen(false)} className="py-2 text-[15px] font-medium text-[#1A3A5F] hover:text-[#00A79D]" data-testid="m-nav-contact">Contact</Link>
                <Link to="/browse" onClick={() => setMobileOpen(false)} className="py-2 text-[15px] font-medium text-[#1A3A5F] hover:text-[#00A79D] inline-flex items-center gap-2"><Search className="w-4 h-4" /> Search directory</Link>
              </div>
              <div className="flex flex-col gap-2.5 pt-1">
                <Link to="/portal" onClick={() => setMobileOpen(false)}
                  className="w-full text-center border border-[#E5E7EB] text-[#1A3A5F] text-sm font-semibold px-4 py-2.5 rounded-md hover:border-[#00A79D]"
                  data-testid="m-public-member-login">Login</Link>
                <Link to="/get-started" onClick={() => setMobileOpen(false)}
                  className="w-full inline-flex items-center justify-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white text-sm font-semibold px-4 py-2.5 rounded-md"
                  data-testid="m-public-cta-header">Get Started <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>
      <CompareTray />
      <MobileCtaBar />

      <footer className="bg-[#111827] text-gray-300 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5">
              <CtcMark className="h-10 w-auto" />
              <Wordmark dark />
            </div>
            <p className="text-sm text-gray-400 mt-3 max-w-xs">Independent care technology guidance. We carry the complexity, so you don't have to.</p>
          </div>
          <div>
            <h4 className="font-heading text-white font-semibold text-sm mb-3">Care providers</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/browse" className="hover:text-[#00A79D]">Browse suppliers</Link></li>
              <li><Link to="/how-to-choose" className="hover:text-[#00A79D]">How to choose</Link></li>
              <li><Link to="/common-problems" className="hover:text-[#00A79D]">Common problems</Link></li>
              <li><Link to="/for-care-providers" className="hover:text-[#00A79D]">For care providers</Link></li>
              <li><Link to="/advisor" className="hover:text-[#00A79D]">CareTechAdvisor</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-white font-semibold text-sm mb-3">Suppliers &amp; sectors</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/for-suppliers" className="hover:text-[#00A79D]">List your product</Link></li>
              <li><Link to="/for-care-services" className="hover:text-[#00A79D]">For care services</Link></li>
              <li><Link to="/for-government" className="hover:text-[#00A79D]">For government</Link></li>
              <li><Link to="/for-membership-orgs" className="hover:text-[#00A79D]">For membership orgs</Link></li>
              <li><Link to="/for-industry-specialists" className="hover:text-[#00A79D]">For industry specialists</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-white font-semibold text-sm mb-3">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/portal" className="hover:text-[#00A79D]">Member login</Link></li>
              <li><Link to="/login" className="hover:text-[#00A79D]" data-testid="footer-staff-login">Staff / CRM login</Link></li>
              <li><Link to="/knowledge-hub" className="hover:text-[#00A79D]">Knowledge Hub</Link></li>
              <li><Link to="/about" className="hover:text-[#00A79D]">About us</Link></li>
              <li><Link to="/contact" className="hover:text-[#00A79D]">Contact</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 pb-24 md:pb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-gray-500">
            <span>© {new Date().getFullYear()} care tech compare. From the team behind CareTechGuide.</span>
            <span className="flex flex-wrap gap-x-4 gap-y-1">
              <Link to="/privacy-policy" className="hover:text-[#00A79D]">Privacy Policy</Link>
              <Link to="/terms-and-conditions" className="hover:text-[#00A79D]">Terms &amp; Conditions</Link>
              <Link to="/terms-of-business" className="hover:text-[#00A79D]" data-testid="footer-terms-of-business">Terms of Business</Link>
              <Link to="/acceptable-use-policy" className="hover:text-[#00A79D]">Acceptable Use</Link>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
